import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

const mobileMediaQuery = window.matchMedia('(max-width: 749px)');

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.refs.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });

    requestAnimationFrame(() => {
      this.#syncVisibleSlideMedia();
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Plays autoplay product media when its mobile gallery slide becomes visible.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    const slide = event.detail.slide;

    if (!(slide instanceof HTMLElement)) return;

    this.#syncVisibleSlideMedia(slide);
  };

  /**
   * Keeps autoplay media scoped to the visible mobile slide.
   * @param {HTMLElement} [activeSlide] - The active slideshow slide.
   */
  #syncVisibleSlideMedia(activeSlide) {
    if (!mobileMediaQuery.matches) return;

    const slides = this.slideshow?.slides;
    if (!slides?.length) return;

    const selectedSlide =
      activeSlide ||
      slides.find((slide) => slide.getAttribute('aria-hidden') === 'false') ||
      slides[this.slideshow?.current || 0];

    for (const slide of slides) {
      const deferredMediaItems = slide.querySelectorAll('deferred-media');

      for (const deferredMedia of deferredMediaItems) {
        if (!('pauseMedia' in deferredMedia) || !('playMedia' in deferredMedia)) continue;

        if (slide !== selectedSlide) {
          deferredMedia.pauseMedia();
          continue;
        }

        if (!deferredMedia.hasAttribute('autoplay')) continue;

        if (!deferredMedia.querySelector('iframe, video') && 'loadContent' in deferredMedia) {
          deferredMedia.loadContent(false);
        }

        deferredMedia.playMedia();
      }
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
